import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { Category } from '@/modules/products/model/types'
import { CATEGORIES as DEFAULT_CATS } from '@/modules/products/config/products.config'

export interface AdminCategory extends Category {
  id: string
  icon: string
  description?: string
  order: number
}

const DEFAULT_ICON_MAP: Record<string, string> = {
  'xe-may-dien':     'bike',
  'xe-dap-dien':     'bike',
  'xe-tay-ga-dien':  'bike',
  'xe-the-thao-dien':'bike',
  'phu-kien':        'wrench',
  'pin-sac':         'battery-charging',
  'do-bao-ho':       'shield',
}

const INITIAL: AdminCategory[] = DEFAULT_CATS.map((c, i) => ({
  id: `c${i + 1}`,
  slug: c.slug,
  label: c.label,
  icon: DEFAULT_ICON_MAP[c.slug] ?? 'package',
  order: (i + 1) * 10,
}))

interface CategoriesState {
  categories: AdminCategory[]
  add: (c: Omit<AdminCategory, 'id'>) => void
  update: (id: string, patch: Partial<AdminCategory>) => void
  remove: (id: string) => void
  move: (id: string, dir: 'up' | 'down') => void
  reset: () => void
  getById: (id: string) => AdminCategory | undefined
}

export const useAdminCategoriesStore = create<CategoriesState>()(
  persist(
    (set, get) => ({
      categories: INITIAL.slice(),
      add: (c) => {
        const id = `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`
        set({ categories: [...get().categories, { ...c, id }].sort((a, b) => a.order - b.order) })
      },
      update: (id, patch) => set({
        categories: get().categories
          .map((x) => (x.id === id ? { ...x, ...patch } : x))
          .sort((a, b) => a.order - b.order),
      }),
      remove: (id) => set({ categories: get().categories.filter((x) => x.id !== id) }),
      move: (id, dir) => {
        const cats = get().categories.slice().sort((a, b) => a.order - b.order)
        const idx = cats.findIndex((c) => c.id === id)
        if (idx < 0) return
        const swapWith = dir === 'up' ? idx - 1 : idx + 1
        if (swapWith < 0 || swapWith >= cats.length) return
        const a = cats[idx]
        const b = cats[swapWith]
        const tmpOrder = a.order
        set({
          categories: cats.map((c) => {
            if (c.id === a.id) return { ...c, order: b.order }
            if (c.id === b.id) return { ...c, order: tmpOrder }
            return c
          }).sort((x, y) => x.order - y.order),
        })
      },
      reset: () => set({ categories: INITIAL.slice() }),
      getById: (id) => get().categories.find((x) => x.id === id),
    }),
    {
      name: 'xedien.admin.categories.v1',
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ categories: s.categories }),
    },
  ),
)
